import { PlusIcon } from '@heroicons/react/24/outline';
import { useQuery } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { FilterBadgesList } from '@/components/FilterBadgesList';
import AccentTitle from '@/components/UI/AccentTitle';
import Button from '@/components/UI/Button';
import ErrorCard from '@/components/UI/ErrorCard';
import LoadingDeveloperApplications from '@/components/UI/loading/LoadingDeveloperApplications';
import { CurrencyFormatDisplay } from '@/components/UI/NumberFormatDisplay/CurrencyFormatDisplay';
import Table from '@/components/UI/Table';
import useEnvironment from '@/hooks/useEnvironment';
import { useFilters } from '@/hooks/useFilters';
import { getPartnerProposals } from '../../../api/Rover/partnerProposal';
import { convertEnumToText, isObjectEmpty } from '../../../utils';
import { AddPartnerProposalsFilterForm } from './AddPartnerProposalsFilterForm';
import PartnerProposalNav from './PartnerProposalNav';
import Styles from './PartnerProposals.module.css';

export default function PartnerProposals() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isProd } = useEnvironment();

  const { showFilterModal, appliedFilters, filterBadges, toggleFilterModal, applyFiltersHandler, removeFilterHandler } = useFilters();

  const [params, setParams] = useState({
    page: 1,
    perPage: 10,
  });

  //Tanstack: version 5 query implementation
  const fetchPartnerProposals = useQuery({
    queryKey: ['get-partner-proposals', { ...params, ...appliedFilters }],
    queryFn: () => getPartnerProposals({ params: { ...params, ...appliedFilters } }),
  });

  const partnerProposals = fetchPartnerProposals.data?.data;
  const partnerProposalsCount = fetchPartnerProposals.data?.meta?.total;

  const totalPage = Math.ceil(Number(partnerProposalsCount) / params.perPage);

  const nextPageHandler = () => {
    if (params.page < totalPage) {
      setParams((prevState) => {
        return {
          ...prevState,
          page: prevState.page + 1,
        };
      });
    }
  };

  const previousPageHandler = () => {
    if (params.page > 1) {
      setParams((prevState) => {
        return {
          ...prevState,
          page: prevState.page - 1,
        };
      });
    }
  };

  const tablePagination = {
    page: params.page,
    pageSize: params.perPage,
    pageCount: totalPage,
    currentPage: params.page,
    setPageSize: (v: number) => setParams((prevState) => ({ ...prevState, perPage: v })),
    nextPage: nextPageHandler,
    previousPage: previousPageHandler,
  };

  const tableColumns: ColumnDef<any>[] = [
    {
      id: 'proposerName',
      header: 'Proposer Name',
      accessorKey: 'proposerName',
      size: 250,
      cell: (props) => {
        const { row } = props;
        return (
          <div className='text-sm text-neutralColor-medium flex flex-col max-w-md'>
            <span className='truncate'>{row.original.proposerName}</span>
            <span className='text-xs text-neutralColor-normal'>{row.original.proposerEmail}</span>
          </div>
        );
      },
    },
    {
      id: 'proposedProperty',
      header: 'Proposed Property',
      accessorKey: 'proposedProperty',
      size: 250,
      cell: (props) => {
        const { row } = props;
        return (
          <div className='flex flex-col gap-1'>
            <span>{row.original.propertyAddress}</span>
            <span className='text-xs text-neutralColor-normal'>
              {row.original.propertyCity}, {row.original.propertyState}
            </span>
            <span className='text-xs text-neutralColor-normal'>{row.original.propertyZipCode}</span>
          </div>
        );
      },
    },
    {
      id: 'developmentCost',
      header: 'Development Cost',
      accessorKey: 'developmentCost',
      size: 200,
      cell: (props) => {
        const { getValue } = props;
        return (
          <div className='flex flex-col gap-1'>
            <CurrencyFormatDisplay value={getValue() as number} prefix='$' />
          </div>
        );
      },
    },
    {
      id: 'acquisitionType',
      header: 'Acquisition Type',
      accessorKey: 'acquisitionType',
      size: 200,
    },
    {
      id: 'constructionType',
      header: 'Construction Type',
      accessorKey: 'constructionType',
      size: 200,
    },
    {
      id: 'dealType',
      header: 'Deal Type',
      accessorKey: 'dealType',
      size: 200,
    },
    {
      id: 'status',
      header: 'Status',
      accessorKey: 'status',
      size: 250,
      cell: (props) => {
        const { getValue } = props;
        const statusValue = getValue() as string;
        return (
          <div className='flex items-center w-full capitalize'>
            <span className={`${Styles[statusValue]}`}>{convertEnumToText(statusValue)}</span>
          </div>
        );
      },
    },
  ];

  return (
    <>
      <div className='flex flex-col w-full'>
        {!isProd && <PartnerProposalNav />}

        <div className='border-neutralColor-light flex justify-between bg-white rounded-t-lg'>
          <AccentTitle title='Partner Proposals' />
        </div>

        <div className='flex flex-col bg-white gap-2 px-4 py-4 border-t'>
          <div className='flex flex-wrap gap-2 items-center'>
            <Button
              label='Add Filter'
              btnStyle='neutral'
              btnSize='md'
              icon={<PlusIcon className='h-5 w-5' />}
              onClick={toggleFilterModal}
            />
            {!isObjectEmpty(filterBadges) && <FilterBadgesList displayFilters={filterBadges} handleRemoveFilter={removeFilterHandler} />}
          </div>
          {!isObjectEmpty(filterBadges) && (
            <span className='m-0 text-neutralColor-normal mb-2'>{partnerProposalsCount} partner proposals found.</span>
          )}
        </div>

        {fetchPartnerProposals.isLoading && <LoadingDeveloperApplications />}
        {fetchPartnerProposals.isSuccess && partnerProposals && (
          <Table
            cols={tableColumns}
            tableData={partnerProposals}
            onRowClick={(row: any) => navigate(`${location.pathname}/${row.id}`)}
            pagination={tablePagination}
          />
        )}
        {!fetchPartnerProposals.isFetching && fetchPartnerProposals.error && (
          <div className='flex-grow flex justify-center items-center text-neutralColor-normal h-screen/2'>
            <ErrorCard
              errorTitle='Fetch Error'
              errorMsg='We are unable to pull the list of partner proposals from the database at this point. Please try again later. '
            />
          </div>
        )}
      </div>
      {showFilterModal && <AddPartnerProposalsFilterForm handleModal={toggleFilterModal} handleApplyFilter={applyFiltersHandler} />}
    </>
  );
}
